import React from "react";
import axios from "axios";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { FaTimes } from "react-icons/fa";
import { I18n } from "react-redux-i18n";
import alertify from "alertifyjs";
import { config } from "../config";
import ReelViewerSlide from "../components/ReelViewerSlide";
import ReelCommentsSheet from "../components/ReelCommentsSheet";
import ModalShareAd from "../components/ModalShareAd";
import MiniLoader from "../components/MiniLoader";
import * as actions_reel from "../redux/actions/reelActions";
import * as actions_ad from "../redux/actions/adActions";
import "../components/styles/Alertify.css";
import "../components/styles/AlertifyTheme.css";
import "../components/styles/Alertify.min.css";
import "../components/styles/AlertifyTheme.min.css";
import "./styles/ReelsViewer.css";

function getSearchParam(search, key) {
  return new URLSearchParams(search || "").get(key);
}

class ReelsViewer extends React.Component {
  state = {
    activeIndex: 0,
    muted: true,
    adCache: {},
    likeCache: {},
    commentsOpen: false,
    commentsProductId: null,
    shareOpen: false,
    shareAd: null,
  };

  slideRefs = [];
  containerRef = React.createRef();
  observer = null;

  componentDidMount() {
    this.bootstrap();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.list !== this.props.list && this.props.list.length) {
      this.syncStartIndex();
      window.requestAnimationFrame(() => this.setupObserver());
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  bootstrap = async () => {
    const { list, user_id, token, location } = this.props;
    const storeId = getSearchParam(location.search, "store_id");

    if (!list.length && user_id && token) {
      await this.props.LoadList(user_id, token, storeId);
    }

    if (this.props.list.length) {
      this.syncStartIndex();
      window.requestAnimationFrame(() => this.setupObserver());
    }
  };

  syncStartIndex = () => {
    const { match, list } = this.props;
    const startId = match.params.reelId;
    if (!startId || !list.length) return;

    const index = list.findIndex((item) => String(item.Id) === String(startId));
    if (index >= 0) {
      this.setState({ activeIndex: index }, () => {
        this.scrollToIndex(index, false);
        this.prefetchAdMeta(index);
      });
    } else {
      this.prefetchAdMeta(0);
    }
  };

  setupObserver = () => {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
          const index = Number(entry.target.dataset.index);
          if (Number.isNaN(index)) return;

          this.setState({ activeIndex: index }, () => {
            this.prefetchAdMeta(index);
          });
        });
      },
      {
        root: this.containerRef.current,
        threshold: [0.6, 0.85],
      }
    );

    this.slideRefs.forEach((node) => {
      if (node) this.observer.observe(node);
    });
  };

  scrollToIndex = (index, smooth = true) => {
    const node = this.slideRefs[index];
    if (!node) return;
    node.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "start" });
  };

  prefetchAdMeta = async (index) => {
    const item = this.props.list[index];
    if (!item?.ProductId) return;

    const productId = String(item.ProductId);
    if (this.state.adCache[productId]) return;

    const { user_id, token } = this.props;
    if (!token) return;

    try {
      const response = await axios.get(`${config.api.godiway}goder/id`, {
        params: {
          goder_id: productId,
          id: user_id,
          lang: I18n.t("locale"),
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status >= 300 || response.data.status !== "Success") {
        return;
      }

      const ad = response.data.body;
      if (!ad) return;

      this.setState((prev) => ({
        adCache: { ...prev.adCache, [productId]: ad },
        likeCache: {
          ...prev.likeCache,
          [productId]: {
            status: ad.Like?.Status ?? null,
            up: ad.Like?.Up ?? 0,
            down: ad.Like?.Down ?? 0,
          },
        },
      }));
    } catch (error) {
      console.log(error);
    }
  };

  requireAuth = () => {
    if (JSON.stringify(this.props.user) === "{}") {
      alertify.alert(I18n.t("warning"), I18n.t("warning_login"), () => {});
      return false;
    }
    return true;
  };

  getProductId = (index = this.state.activeIndex) => {
    return this.props.list[index]?.ProductId;
  };

  updateLikeCache = (productId, updater) => {
    this.setState((prev) => ({
      likeCache: {
        ...prev.likeCache,
        [productId]: updater(prev.likeCache[productId] || { status: null, up: 0, down: 0 }),
      },
    }));
  };

  onLike = async () => {
    if (!this.requireAuth()) return;

    const productId = String(this.getProductId());
    if (!productId) return;

    const current = this.state.likeCache[productId] || { status: null, up: 0, down: 0 };
    const { user_id, token } = this.props;

    if (current.status === 1) {
      this.updateLikeCache(productId, (prev) => ({
        status: null,
        up: Math.max(0, prev.up - 1),
        down: prev.down,
      }));
      await this.props.RemoveLike(token, { goder_id: productId, id: user_id });
      return;
    }

    this.updateLikeCache(productId, (prev) => ({
      status: 1,
      up: prev.up + 1,
      down: current.status === 0 ? Math.max(0, prev.down - 1) : prev.down,
    }));

    await this.props.UpdateLike(token, {
      goder_id: productId,
      status: "1",
      id: user_id,
    });
  };

  onDislike = async () => {
    if (!this.requireAuth()) return;

    const productId = String(this.getProductId());
    if (!productId) return;

    const current = this.state.likeCache[productId] || { status: null, up: 0, down: 0 };
    const { user_id, token } = this.props;

    if (current.status === 0) {
      this.updateLikeCache(productId, (prev) => ({
        status: null,
        up: prev.up,
        down: Math.max(0, prev.down - 1),
      }));
      await this.props.RemoveLike(token, { goder_id: productId, id: user_id });
      return;
    }

    this.updateLikeCache(productId, (prev) => ({
      status: 0,
      down: prev.down + 1,
      up: current.status === 1 ? Math.max(0, prev.up - 1) : prev.up,
    }));

    await this.props.UpdateLike(token, {
      goder_id: productId,
      status: "'0'",
      id: user_id,
    });
  };

  onToggleMute = () => {
    this.setState((prev) => ({ muted: !prev.muted }));
  };

  onOpenComments = () => {
    const productId = this.getProductId();
    if (!productId) return;
    this.setState({ commentsOpen: true, commentsProductId: productId });
  };

  onCloseComments = () => {
    this.setState({ commentsOpen: false, commentsProductId: null });
  };

  onOpenShare = () => {
    const item = this.props.list[this.state.activeIndex];
    if (!item?.ProductId) return;

    this.setState({
      shareOpen: true,
      shareAd: {
        Id: item.ProductId,
        Name: item.Title,
      },
    });
  };

  onCloseShare = () => {
    this.setState({ shareOpen: false, shareAd: null });
  };

  onGoNext = () => {
    const next = Math.min(this.state.activeIndex + 1, this.props.list.length - 1);
    this.scrollToIndex(next);
  };

  onGoPrev = () => {
    const prev = Math.max(this.state.activeIndex - 1, 0);
    this.scrollToIndex(prev);
  };

  onClose = () => {
    if (this.props.history.length > 1) {
      this.props.history.goBack();
    } else {
      this.props.history.push("/");
    }
  };

  setSlideRef = (index) => (node) => {
    this.slideRefs[index] = node;
  };

  render() {
    const { list, loading } = this.props;
    const {
      activeIndex,
      muted,
      adCache,
      likeCache,
      commentsOpen,
      commentsProductId,
      shareOpen,
      shareAd,
    } = this.state;

    if (loading && !list.length) {
      return (
        <div className="reels-viewer reels-viewer--loading">
          <MiniLoader />
        </div>
      );
    }

    if (!list.length) {
      return (
        <div className="reels-viewer reels-viewer--empty">
          <button type="button" className="reels-viewer__close" onClick={this.onClose}>
            <FaTimes />
          </button>
          <p>{I18n.t("reels_empty")}</p>
        </div>
      );
    }

    return (
      <div className="reels-viewer">
        <button
          type="button"
          className="reels-viewer__close"
          onClick={this.onClose}
          aria-label={I18n.t("close")}
        >
          <FaTimes />
        </button>

        <div className="reels-viewer__feed" ref={this.containerRef}>
          {list.map((item, index) => {
            const productId = String(item.ProductId || "");
            return (
              <div
                key={item.Id}
                className="reels-viewer__slide-wrap"
                data-index={index}
                ref={this.setSlideRef(index)}
              >
                <ReelViewerSlide
                  item={item}
                  isActive={index === activeIndex}
                  muted={muted}
                  onToggleMute={this.onToggleMute}
                  likeState={likeCache[productId]}
                  adMeta={adCache[productId]}
                  onLike={this.onLike}
                  onDislike={this.onDislike}
                  onOpenComments={this.onOpenComments}
                  onOpenShare={this.onOpenShare}
                  onGoNext={this.onGoNext}
                  onGoPrev={this.onGoPrev}
                  hasNext={index < list.length - 1}
                  hasPrev={index > 0}
                />
              </div>
            );
          })}
        </div>

        <ReelCommentsSheet
          isOpen={commentsOpen}
          productId={commentsProductId}
          onClose={this.onCloseComments}
        />

        <ModalShareAd isOpen={shareOpen} onClose={this.onCloseShare} ad={shareAd || {}} />
      </div>
    );
  }
}

function mapStateToProps(reducer) {
  return {
    user_id: reducer.session.auth.id,
    token: reducer.session.auth.access_token,
    user: reducer.session.auth,
    list: reducer.reel.list,
    loading: reducer.reel.loading,
  };
}

const mapDispatchToProps = (dispatch) => ({
  LoadList: (user_id, token, store_id) =>
    dispatch(actions_reel.loadList({ user_id, token, store_id })),
  UpdateLike: (token, params) => dispatch(actions_ad.updateLike({ token, params })),
  RemoveLike: (token, params) => dispatch(actions_ad.removeLike({ token, params })),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ReelsViewer));
